// eslint-disable-next-line import/named
import { asMarkdownTemplate } from '../../../webmodule-common/other/general/markdown';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { EventTemplate, Snippet } from '../../../webmodule-common/other/ui/events';
import {
  fetchQuoteAsset,
  getV6QuoteItemForContainer,
  quoteItemAssets,
  splitV6QuoteItemAfterCommit,
  updateQuoteItemContainer
} from '../data/v6/helper-functions';
import { flagInSet } from '../../../webmodule-common/other/ui/string-helper-functions';
import { FormInputAssistantBase } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { getBranchQuoteDefaultsForSupplier } from '../../dealer-franchisee/quotes/data/v6/branch-quote-defaults';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { html } from 'lit';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import { isFrame } from '../data/quote-helper-functions';
import { isSupplierAgent } from '../../../webmodule-common/other/currentuser-claims';
import { v6Editors } from '../../../webmodule-common/interop/interfaces/v6-config';
import { isValidV6ConfigVersion } from '../../../webmodule-common/interop/interfaces/v6-config';
import { v6SupportsVersion } from '../../../webmodule-common/interop/interfaces/v6-config';
import { v6VersionMap } from '../../../webmodule-common/interop/interfaces/v6-config';
import { LangV6QuoteSupplierDefaultsView } from '../../../localization/scripts/quotes/v6/v6-quote-supplier-defaults-view-lang';
import {
  QuoteAttributesDataProvider,
  QuoteDefaultsDataProvider,
  QuoteFrameItemMarker,
  ResultQuoteFrameItemUpdated,
  V6QuoteAttributesEditor,
  V6QuoteDefaultsEditor
} from '../../../webmodule-common/interop/webmodule-interop';
import { QuoteState } from '../../api/dealer-api-interface-quote';
import { QuoteSupplierDefaultsView, QuoteSupplierDefaultsViewOptions } from '../views/quote-supplier-defaults.view';
import { toJsonStr } from '../../../webmodule-common/other/blob-converters';

//
export class V6QuoteSupplierDefaultsView
  extends QuoteSupplierDefaultsView
  implements QuoteDefaultsDataProvider, QuoteAttributesDataProvider
{
  v6editor: V6QuoteDefaultsEditor | null;
  v6AttributesEditor: V6QuoteAttributesEditor | null = null;

  constructor(options: QuoteSupplierDefaultsViewOptions) {
    super(options);

    this.v6AttributesEditor =
      isValidV6ConfigVersion() && v6SupportsVersion(v6VersionMap.hasGlobalAttributesEditor)
        ? v6Editors().getQuoteAttributesEditorService(this)
        : null;
    this.v6editor =
      this.v6AttributesEditor === null && isValidV6ConfigVersion()
        ? v6Editors().getQuoteDefaultsEditorService(this)
        : null;
    if (!this.v6editor && !this.v6AttributesEditor) console.warn('V6QuoteSupplierDefaultsView: No V6 Editor available');
  }

  async getBaseLineQuoteDefaults() {
    return await getBranchQuoteDefaultsForSupplier(
      this.quoteManager.quote.supplierId,
      this.supplierReferenceOverrideKey,
      getCurrentUser()?.id ?? ''
    );
  }

  public async refresh(): Promise<void> {
    await this.v6editor?.refresh?.();
    await this.v6AttributesEditor?.refresh?.();
  }

  get supplierReferenceOverrideKey(): string {
    return this.quoteManager.quote.quoteOwnerId;
  }

  get v6UI(): HTMLElement | null {
    return this.v6AttributesEditor?.ui ?? this.v6editor?.ui ?? null;
  }

  get readonly(): boolean {
    return this.v6UI === null || this.quoteManager.isReadonly() || this.areItemsOpen();
  }

  get quoteIGUs(): unknown {
    return this.getQuoteIGUs();
  }

  private get hasSupplierPriceAdjustments(): boolean {
    return (
      this.quoteManager.container.itemPrices?.some(
        x => x.supplierPriceAdjustmentEnabled && x.supplierPriceAdjustment !== 0
      ) ?? false
    );
  }

  async updateQuoteItemsBegin(): Promise<boolean> {
    return true;
  }

  applyAttributesHeaderTemplate() {
    const explanation = LangV6QuoteSupplierDefaultsView.infoAttributesHeader();
    return this.hasSupplierPriceAdjustments
      ? html` <div class="alert alert-info m-3" role="alert">${asMarkdownTemplate(explanation)}</div>`
      : undefined;
  }

  applyQuoteDefaultsHeaderTemplate() {
    const explanation = LangV6QuoteSupplierDefaultsView.infoQuoteDefaultsHeader();
    return this.hasSupplierPriceAdjustments
      ? html` <div class="alert alert-info m-3" role="alert">${asMarkdownTemplate(explanation)}</div>`
      : undefined;
  }

  async updateQuoteItemsEnd(updatedCount: number): Promise<void> {
    if (updatedCount > 0) {
      await this.quoteManager.runAndSaveSSI();
    }
  }

  async dispose(): Promise<void> {
    await super.dispose();
    //add nullable to deal with version missing info
    if (this.v6UI) this.v6UI.remove();
    await this.v6AttributesEditor?.releaseResources?.();
    await this.v6editor?.releaseResources?.();
    this.v6editor = null;
    this.v6AttributesEditor = null;
    if (!this.v6editor && !this.v6AttributesEditor) console.warn('V6QuoteSupplierDefaultsView: No V6 Editor available');
  }

  async quoteItems(): Promise<QuoteFrameItemMarker[]> {
    await this.quoteManager.needsQuoteItems();
    const frames = this.quoteManager.container.items
      ?.filter(x => isFrame(x))
      .map(x => {
        const container = this.quoteManager.quoteItemContainer(x.id);
        const v6Item = getV6QuoteItemForContainer(container);

        // Defensive: if this frame has no V6 data (corrupt/missing providerData),
        // exclude it from the list entirely. Consumers of QuoteFrameItemMarker
        // expect every marker to be valid and cannot handle null/empty quoteItem.
        if (!v6Item) {
          return null;
        }

        const qfd: QuoteFrameItemMarker = {
          id: x.id,
          description: container.item.title,
          description2: container.item.description,
          qty: container.item.quantity,
          position: this.quoteManager.itemPosition(x.id) ?? -1,

          quoteItem: () => {
            return v6Item;
          },
          fetchAsset: async (assetType: string): Promise<string | object | null> => {
            if (assetType === 'model3d') {
              const legacyData = splitV6QuoteItemAfterCommit(getV6QuoteItemForContainer(container));
              //legacy data
              const data = legacyData.vrml;
              if (data && typeof data === 'string' && data.trim().length > 0) {
                return data;
              }
              //new data
              const c = this.quoteManager.quoteItemContainer(x.id);
              return (
                ((await fetchQuoteAsset({
                  quoteId: x.quoteId,
                  quoteItemId: x.id,
                  assetPath: c.data?.assetVirtualPaths['model3d']?.path,
                  publicUrl: c.data?.assetVirtualPaths['model3d']?.publicUrl
                })) as string) ?? ''
              );
            } else if (assetType === 'data') {
              return getV6QuoteItemForContainer(container) as any;
            }
            return null;
          },
          hasSupplierAdjustment:
            container.price.supplierPriceAdjustmentEnabled && container.price.supplierPriceAdjustment !== 0
        };
        return qfd;
      })
      .filter((x): x is QuoteFrameItemMarker => x !== null);
    return frames ?? [];
  }

  async saveItem(frameData: ResultQuoteFrameItemUpdated): Promise<boolean> {
    const container = this.quoteManager.quoteItemContainer(frameData.id);
    const dataVer = container.data?.recordVersion ?? '';

    if (!frameData.quoteItem) {
      throw new DevelopmentError(
        'V6QuoteSupplierDefaultsView: frameData.quoteItem is null or undefined; skipping V6 frame update.'
      );
    }

    const buyInCost = await this.quoteManager.getBuyInCosts(frameData.quoteItem['buyIn']);

    const v6QuoteItemStorage = toJsonStr(frameData.quoteItem) ?? null;
    updateQuoteItemContainer(container, v6QuoteItemStorage, frameData.price, buyInCost);
    const thumb = isFrame(container.item) ? (frameData.thumbnail ?? null) : null;
    const newContainer = await this.quoteManager.saveAndUpdateQuoteItem(
      container,
      quoteItemAssets({
        data: v6QuoteItemStorage,
        thumbnail: thumb,
        bomView: frameData.bomView,
        model3d: frameData.vrml
      })
    );
    return dataVer !== newContainer.data?.recordVersion;
  }

  getQuoteDefaults(): unknown {
    return this.getQuoteDefaultOptions();
  }

  saveQuoteDefaults(quoteDefaultsData: unknown): boolean {
    this.setQuoteDefaultOptions(quoteDefaultsData);
    return true;
  }

  saveAttributeUpdatedHistory(quoteAttributeHistoryData: unknown): boolean {
    this.setQuoteAttributeUpdateHistory(quoteAttributeHistoryData);
    return true;
  }

  getAttributeUpdatedHistory(): unknown {
    return this.getQuoteAttributeUpdateHistory();
  }

  getGlobalAttributesMasterSet(): unknown {
    return this.getQuoteGlobalAttributesMasterSet();
  }

  saveGlobalAttributesMasterSet(quoteDefaultsData: unknown): boolean {
    this.setQuoteGlobalAttributesMasterSet(quoteDefaultsData);
    return true;
  }

  async prepareForSave() {
    await this.v6editor?.prepareForSave();
    await this.v6AttributesEditor?.prepareForSave();
  }

  public async afterConstruction(): Promise<void> {
    await super.afterConstruction();
    //override to do any asyn post construction work
  }

  buttonMenu(): Snippet {
    const form = new FormInputAssistantBase();
    const resetEvent = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();

      if (this.v6AttributesEditor) await this.v6AttributesEditor.resetQuoteDefaults();
      else await this.v6editor?.resetQuoteDefaults();
    };
    const applyToItems = async (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.areItemsOpen()) {
        await information(LangV6QuoteSupplierDefaultsView.pleaseCloseQuoteItem());
        return;
      }

      await this.prepareForSave();
      if (this.quoteManager.changed()) await this.quoteManager.saveQuote();
      if (this.v6AttributesEditor) await this.v6AttributesEditor.applyChanges();
      else await this.v6editor?.applyQuoteDefaultsToItems();
    };
    const title = this.v6AttributesEditor
      ? LangV6QuoteSupplierDefaultsView.applyChanges()
      : LangV6QuoteSupplierDefaultsView.applyToAllExistingFrame();
    const applyChangesButton = form.buttonPrimary(title, {
      size: 'small',
      events: { click: applyToItems },
      readonly: !this.canModify() || this.readonly
    });

    const resetDefaultsButton = form.buttonDefault(LangV6QuoteSupplierDefaultsView.resetDefaults(), {
      size: 'small',
      events: { click: resetEvent },
      readonly: !this.canModify() || this.readonly
    });
    return this.v6UI !== null ? html`${resetDefaultsButton}${applyChangesButton} ` : html``;
  }

  protected getQuoteIGUs(): unknown {
    //TODO, get these from the quote manager
    return [];
  }

  protected canModify() {
    return (
      this.quote.state === QuoteState.Draft ||
      this.quote.state === QuoteState.Active ||
      (isSupplierAgent() &&
        // eslint-disable-next-line no-bitwise
        flagInSet(this.quote.state, QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending))
    );
  }

  protected getQuoteDefaultOptions(): unknown | null {
    return null;
  }

  protected getQuoteGlobalAttributesMasterSet(): unknown | null {
    return null;
  }

  protected getQuoteAttributeUpdateHistory(): unknown | null {
    return null;
  }

  protected setQuoteDefaultOptions(_options: unknown) {
    throw new DevelopmentError('setQuoteDefaultOptions must override');
  }

  protected setQuoteAttributeUpdateHistory(_options: unknown) {
    throw new DevelopmentError('setQuoteDefaultOptions must override');
  }

  protected setQuoteGlobalAttributesMasterSet(_options: unknown) {
    throw new DevelopmentError('setQuoteDefaultOptions must override');
  }

  protected template(): EventTemplate {
    const ui = this.v6UI;
    return ui ? html`${ui}` : html`${LangV6QuoteSupplierDefaultsView.supplierServiceNotAvailable()}`;
  }
}
